import React, { useState, useEffect, useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Write your content here...",
  readOnly = false,
}) => {
  const [useTextarea, setUseTextarea] = useState(false);
  const editorRef = useRef<any>(null);

  useEffect(() => {
    // Check if TinyMCE loads successfully
    const timer = setTimeout(() => {
      if (!editorRef.current) {
        setUseTextarea(true);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (useTextarea) {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold h-64"
      />
    );
  }

  return (
    <div className="rich-text-editor">
      <Editor
        apiKey="m9jlrdfeprpgwcaphu8z6eogc8gumo4xuaw7ug29zhwmamac"
        onInit={(evt, editor) => {
          editorRef.current = editor;
        }}
        value={value}
        onEditorChange={(content) => onChange(content)}
        init={{
          height: 400,
          menubar: true,
          plugins: [
            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
            'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount',
            'emoticons', 'paste', 'textpattern', 'nonbreaking', 'pagebreak',
            'quickbars', 'template', 'codesample', 'hr', 'directionality'
          ],
          toolbar: [
            'undo redo | formatselect | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify',
            'bullist numlist outdent indent | removeformat | subscript superscript | link image media table | code fullscreen help'
          ].join(' | '),
          toolbar_mode: 'wrap',
          content_style: `
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; 
              font-size: 14px; 
              line-height: 1.6;
              color: #333;
            }
            h1, h2, h3, h4, h5, h6 { 
              margin-top: 1.5em; 
              margin-bottom: 0.5em; 
              font-weight: 600; 
            }
            h1 { font-size: 2em; }
            h2 { font-size: 1.5em; }
            h3 { font-size: 1.25em; }
            p { margin-bottom: 1em; }
            ul, ol { margin-bottom: 1em; padding-left: 2em; }
            blockquote { 
              border-left: 4px solid #e5e7eb; 
              padding-left: 1em; 
              margin: 1em 0; 
              font-style: italic; 
            }
            a { color: #d97706; text-decoration: none; }
            a:hover { text-decoration: underline; }
            img { max-width: 100%; height: auto; }
            table { border-collapse: collapse; width: 100%; margin: 1em 0; }
            th, td { border: 1px solid #d1d5db; padding: 0.5em; text-align: left; }
            th { background-color: #f9fafb; font-weight: 600; }
          `,
          placeholder: placeholder,
          branding: false,
          promotion: false,
          paste_data_images: true,
          image_advtab: true,
          link_list: [
            {title: 'My page 1', value: 'https://www.tiny.cloud'},
            {title: 'My page 2', value: 'http://www.moxiecode.com'}
          ],
          image_list: [
            {title: 'My image 1', value: 'https://www.tiny.cloud/myimage1.jpg'},
            {title: 'My image 2', value: 'http://www.moxiecode.com/myimage2.jpg'}
          ],
          file_picker_callback: function (callback: any, value: any, meta: any) {
            // Custom file picker for images
            if (meta.filetype === 'image') {
              const input = document.createElement('input');
              input.setAttribute('type', 'file');
              input.setAttribute('accept', 'image/*');
              input.onchange = function () {
                const file = (input.files as FileList)[0];
                const reader = new FileReader();
                reader.onload = function () {
                  callback(reader.result as string, { title: file.name });
                };
                reader.readAsDataURL(file);
              };
              input.click();
            }
          },
          setup: function (editor: any) {
            // Add custom functionality here if needed
            editor.on('change', function () {
              // Handle editor changes
            });
          }
        }}
      />
    </div>
  );
};

export default RichTextEditor;
